import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { RawMessage } from './feedback';
import { Button, Icon } from './primitives';

/**
 * Focus containment for a modal, plus putting focus back where it came from.
 *
 * A dialog that leaks focus to the page behind it is worse than no dialog: the scrim says
 * "nothing back there is reachable" and Tab immediately proves otherwise. Returning focus to
 * the trigger matters just as much — without it, dismissing a dialog drops a keyboard user at
 * the top of the document, several Tabs away from the row they were working on.
 */
export function useModal(open: boolean, initialFocus?: React.RefObject<HTMLElement | null>) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const restoreTo = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(false);

  // Where focus was, taken during the render that opens the dialog rather than in the effect below.
  // By the time any effect runs the dialog is in the document, and a field carrying `autoFocus` has
  // already taken focus — so reading `activeElement` there recorded the dialog's own field, which
  // is gone by the time focus is handed back, and a keyboard user landed on the top of the page.
  // During this render nothing of the dialog exists yet, and focus is still on the trigger.
  if (open && !wasOpen.current && typeof document !== 'undefined') {
    restoreTo.current = document.activeElement as HTMLElement | null;
  }
  wasOpen.current = open;

  useEffect(() => {
    if (!open) return;
    // Unless what was focused then is nothing worth returning to. A dialog opened by another one
    // closing — a code's reveal straight after the form that created it — renders while the first
    // dialog still holds focus somewhere it is about to lose: on a button removed in this same
    // commit, or on the page body, where the browser put it when the submit disabled the field
    // that had it. By now the first dialog has handed focus back to its own trigger, which is the
    // right place for this one to return to as well.
    const recorded = restoreTo.current;
    if (!recorded || !recorded.isConnected || recorded === document.body) {
      restoreTo.current = document.activeElement as HTMLElement | null;
    }
    // Optional, because a dialog that opens onto a form wants its first *field* focused and
    // says so with `autoFocus` on that field — which has run by the time this effect does, and
    // which would be undone by focusing anything from here. Restoring focus to the trigger on
    // the way out is the half every dialog needs either way.
    initialFocus?.current?.focus();
    return () => restoreTo.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key !== 'Tab') return;
    const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
      'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
    );
    if (!focusable || focusable.length === 0) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }, []);

  return { dialogRef, onKeyDown };
}

export interface ConfirmDialogProps {
  open?: boolean;
  title: string;
  /** Prose: what is destroyed, and for whom. Not "Are you sure?". */
  children?: ReactNode;
  /** A verb. Never "OK". */
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  /** Typed back exactly before the confirm button enables. The team's name, for deletion. */
  typeToConfirm?: string;
  busy?: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
}

/**
 * The one confirmation pattern in the dashboard.
 *
 * This replaces every `window.confirm`, which gets a single line of unstyled text in browser
 * chrome — the wrong shape for the things this app destroys. Deleting a team takes its
 * memberships, invites, package prefixes and CI tokens with it, and that list *is* the
 * decision. `window.confirm` also cannot ask for anything, so the irreversible actions and the
 * recoverable ones looked identical.
 *
 * Cancel holds initial focus: Enter and Space are what a keyboard reaches for first, and the
 * safe answer should be the one underneath them.
 */
export function ConfirmDialog({
  open = false,
  title,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = true,
  typeToConfirm,
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState('');
  const cancelRef = useRef<HTMLButtonElement>(null);
  const headingId = useId();
  const { dialogRef, onKeyDown } = useModal(open, cancelRef);

  useEffect(() => {
    if (!open) setTyped('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;
  const ready = !typeToConfirm || typed.trim() === typeToConfirm;

  return (
    <div
      className="bf-scrim"
      // Mousedown, so releasing outside after selecting text inside does not count as a
      // click on the scrim and throw away what was being read.
      onMouseDown={(event) => { if (event.target === event.currentTarget) onCancel?.(); }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        className="bf-dialog"
        onKeyDown={onKeyDown}
      >
        <h2 id={headingId} className="bf-dialog-title">{title}</h2>
        <div className="bf-dialog-body">{children}</div>

        {typeToConfirm ? (
          <label className="bf-field">
            <span className="bf-field-label">
              Type <code className="bf-inline-code">{typeToConfirm}</code> to confirm
            </span>
            <input
              className="bf-input is-mono"
              value={typed}
              autoComplete="off"
              onChange={(event) => setTyped(event.target.value)}
            />
          </label>
        ) : null}

        <div className="bf-dialog-actions">
          <Button ref={cancelRef} variant="soft" size="md" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? 'danger' : 'primary'}
            size="md"
            disabled={!ready || busy}
            onClick={onConfirm}
          >
            {busy ? 'Working…' : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

export interface FormDialogProps {
  open?: boolean;
  title: string;
  /** One or two sentences above the fields: what submitting does, and to whom. */
  note?: ReactNode;
  /** The fields. The first one should carry `autoFocus` — see `useModal`. */
  children?: ReactNode;
  /** A verb for what submitting does — "Give access", "Create code". Never "OK". */
  submitLabel: string;
  /** Whether submitting would do anything yet. The button stays disabled until it would. */
  ready?: boolean;
  busy?: boolean;
  /** Why the last submit failed, in the server's words. Shown inside the dialog, beside the form. */
  error?: string | null;
  onSubmit: () => void;
  onCancel: () => void;
}

/**
 * A small form in a dialog — the one-field-or-two "add" that sits behind a button on a list.
 *
 * The lists it serves used to carry their forms above them, always open: a channel's Access tab was
 * two forms and two lists stacked, and the list — the thing somebody opens the tab to read — was
 * the second panel down. The form is something you do now and then; the list is what you check.
 *
 * The error is shown **in** the dialog rather than in a banner on the page behind it, because the
 * page is under a scrim and the fix is a field in here. Escape and a click on the scrim cancel,
 * except while a submit is in flight — closing then would leave somebody unsure whether it went.
 */
export function FormDialog({
  open = false,
  title,
  note,
  children,
  submitLabel,
  ready = true,
  busy = false,
  error,
  onSubmit,
  onCancel,
}: FormDialogProps) {
  const headingId = useId();
  const { dialogRef, onKeyDown } = useModal(open);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busy) onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  return (
    <div
      className="bf-scrim"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onCancel();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        className="bf-dialog"
        onKeyDown={onKeyDown}
      >
        <h2 id={headingId} className="bf-dialog-title">{title}</h2>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (ready && !busy) onSubmit();
          }}
        >
          <div className="bf-dialog-body">
            {note ? <p className="bf-dialog-note">{note}</p> : null}
            {children}
            {error ? <p className="bf-field-error">{error}</p> : null}
          </div>
          <div className="bf-dialog-actions">
            <Button variant="soft" size="md" type="button" disabled={busy} onClick={onCancel}>
              Cancel
            </Button>
            <Button variant="primary" size="md" type="submit" disabled={!ready || busy}>
              {busy ? 'Working…' : submitLabel}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export interface MessageDialogProps {
  open?: boolean;
  title: string;
  /** Who or what said it, and when. One line above the text. */
  note?: ReactNode;
  /** The label on the block itself — `RawMessage`'s own. */
  label?: ReactNode;
  children?: ReactNode;
  onClose?: () => void;
}

/**
 * Somebody else's text, verbatim, on its own.
 *
 * The install feed is the caller and the reason this exists. Every failed install carries a
 * paragraph of Board's own words — `INSTALL_PARSE_FAILED_NO_CERTIFICATES: Failed to collect
 * certificates from /data/app/…` — and printing all of them under all fifty rows made the one
 * that mattered impossible to find: the list stopped being a list and became a wall, and the
 * *shape* of the failures (four of these, one of those) was the thing it was being read for.
 *
 * So the feed marks the rows that have one and this is where it is read. **Nothing about the text
 * changes** — not truncated, not summarised, not prettified, selectable, and the whole of it —
 * which is the rule `RawMessage` states and this dialog exists to keep while moving where it is
 * printed.
 *
 * Escape and the scrim both dismiss, unlike `SecretReveal`: there is nothing here that cannot be
 * opened again from the row it came from.
 */
export function MessageDialog({
  open = false,
  title,
  note,
  label,
  children,
  onClose,
}: MessageDialogProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const headingId = useId();
  const { dialogRef, onKeyDown } = useModal(open, closeRef);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="bf-scrim"
      // Mousedown, so releasing outside after selecting the message does not count as a click on
      // the scrim and close the thing being copied. This dialog exists to be copied out of.
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        className="bf-dialog"
        onKeyDown={onKeyDown}
      >
        <h2 id={headingId} className="bf-dialog-title">{title}</h2>
        {note ? <p className="bf-dialog-note">{note}</p> : null}
        <RawMessage label={label}>{children}</RawMessage>
        <div className="bf-dialog-actions">
          <Button ref={closeRef} variant="soft" size="md" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

export interface SecretRevealProps {
  open?: boolean;
  title?: string;
  note?: string;
  secret?: string | null;
  secretLabel?: string;
  link?: string | null;
  linkLabel?: string;
  onDone?: () => void;
}

/**
 * The one-time reveal.
 *
 * Redeem codes, invite links and CI tokens are stored only as SHA-256 hashes and returned by
 * the API exactly once, so this is the only moment they exist in readable form. It blocks the
 * page and has a single way out: no Escape, no scrim click, nothing behind it reachable and
 * nothing to scroll past. Anything that dismisses a modal by accident would here destroy a
 * value nobody can get back.
 *
 * Never render one of these into a list, a toast, or a page you can navigate away from.
 */
export function SecretReveal({
  open = false,
  title = 'Copy this now',
  note = 'Stored only as a hash, and shown once, here.',
  secret,
  secretLabel,
  link,
  linkLabel = 'Send them this:',
  onDone,
}: SecretRevealProps) {
  const [copied, setCopied] = useState<'link' | 'code' | 'failed' | null>(null);
  const doneRef = useRef<HTMLButtonElement>(null);
  const headingId = useId();
  const { dialogRef, onKeyDown } = useModal(open, doneRef);

  useEffect(() => {
    if (!open) setCopied(null);
  }, [open]);

  async function copy(text: string, which: 'link' | 'code') {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
    } catch {
      // The clipboard is refusable — insecure context, permissions, a browser that just says
      // no. Saying so is the whole point: the text stays selectable, and a silent failure here
      // costs someone a value they cannot ask for again.
      setCopied('failed');
    }
  }

  if (!open) return null;

  return (
    <div className="bf-scrim is-blocking">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        className="bf-dialog is-reveal"
        onKeyDown={onKeyDown}
      >
        <div className="bf-reveal-head">
          <Icon name="lock" size={26} style={{ color: 'var(--accent)' }} />
          <h2 id={headingId} className="bf-dialog-title">{title}</h2>
        </div>
        <p className="bf-dialog-note">{note}</p>

        {secret ? (
          <div className="bf-reveal-group">
            {secretLabel ? <span className="bf-field-label">{secretLabel}</span> : null}
            <div className="bf-reveal-box is-secret">{secret}</div>
          </div>
        ) : null}

        {link ? (
          <div className="bf-reveal-group">
            <span className="bf-field-label">{linkLabel}</span>
            <div className="bf-reveal-box">{link}</div>
          </div>
        ) : null}

        <div className="bf-reveal-actions">
          {link ? (
            <Button variant="primary" size="md" icon="link" onClick={() => copy(link, 'link')}>
              Copy link
            </Button>
          ) : null}
          {secret ? (
            <Button variant="soft" size="md" onClick={() => copy(secret, 'code')}>
              Copy code
            </Button>
          ) : null}
          <span className="bf-grow" />
          <Button ref={doneRef} variant="soft" size="md" onClick={onDone}>
            Done
          </Button>
        </div>

        {/* "Did that work?" is otherwise unanswerable, and the answer has to be spoken too. */}
        <div className={copied === 'failed' ? 'bf-reveal-ack is-failed' : 'bf-reveal-ack'} role="status">
          {copied === 'failed' ? 'Could not reach the clipboard. Select the text and copy it.'
            : copied === 'link' ? 'Link copied.'
            : copied === 'code' ? 'Code copied.' : ''}
        </div>
      </div>
    </div>
  );
}
